import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { toast } from 'd3components/src/utils/tools';
import { h, createApp, reactive } from 'vue';
import Dialog from '../../layout/components/dialog.vue';
import { checkUpgradeDirExists, D3_BASE_PATH, getPlatformConfig } from '../file/constants';
import { createFile } from '../file/createfile';
import { i18n } from '@/i18n';
import {
  getAndroidStoragePermissionStatus,
  requestAndroidStoragePermission,
} from '@/capacitor/permissions/android-storage-permission';

// 外部下发的版本信息（version.json）
// 用于指示设备应下载并应用的前端资源包。
export interface VersionInfo {
  // 目标版本号（建议提供）。
  // - 存在时：与当前内置版本对比（compareVersions）决定是否更新；同时参与"是否已应用"的判断。
  // - 缺省时：系统会基于 downloadUrl | updateTime 生成 remote-<hash> 作为目标版本，依赖签名变化判定更新。
  version?: string;
  // 资源包下载地址（必填）。
  // - 指向可访问的 zip（如 dist.zip）。
  // - 服务端应支持 HEAD 返回稳定指纹（ETag 或 Content-Length/Last-Modified）。
  downloadUrl: string;
  // 更新时间（可选，强烈建议提供）。
  updateTime?: string;
  // files?: Record<string, string>;
  // 版本描述
  description?: string;
  // 下载进度百分比 (0-100)
  downloadPercent?: number;
}

// 热更新运行时配置。
export interface HotUpdateConfig {
  // 外部存储根目录名（位于 Directory.ExternalStorage 下）。
  // - 用于存放 version.json、state.json 。
  // - 默认：Download/D3（设备Download文件夹下的D3文件夹）
  externalStoragePath: string;

  // 版本指令文件名。
  // - 默认：version.json
  versionFileName: string;
  // 周期检查间隔（毫秒）。
  // - 实际调度时附带 ±10% 抖动，避免设备同刻并发拉取。
  // - 默认：30 * 60 * 1000（30 分钟）
  checkInterval: number;

  // 最大重试次数（预留）。
  // - 当前实现未用于下载重试，可用于未来扩展。
  // maxRetries: number;

  // 是否启用定时检查。
  // - true：首检后继续周期性检查；false：仅在初始化时检查一次。
  enabled: boolean;

  // 更新确认模式：
  // - silent: 静默（不提示，直接更新）
  // - before-download: 下载前询问（默认）
  // - after-download: 下载完成后应用前询问（预留，当前与 before-download 同行为）
  // - force: 强制更新（不允许取消）
  confirmMode?: 'silent' | 'before-download' | 'after-download' | 'force';

  // 自定义确认处理器：返回 accept / decline / later
  // 若未提供，则使用内置 Vue Dialog（defaultConfirmHandler）
  confirmHandler?: (info: VersionInfo) => Promise<'accept' | 'decline' | 'later'>;

  // Capgo 下载事件回调（可选，用于外部监听进度/状态）
  onDownloadEvent?: (state: any) => void;

  // 下载进度回调（可选，percent 为 0-100）
  onDownloadProgress?: (percent: number, state?: any) => void;
}

// 热更新 UI 交互阶段
// - prompt: 发现新版本，等待用户确认
// - storage-permission: 用户已确认，正在申请/引导存储权限（仅 Android）
// - downloading: 正在下载并应用资源包
// - failed: 下载或应用失败，等待用户重试或取消
type HotUpdateUiStage = 'prompt' | 'storage-permission' | 'downloading' | 'failed';

// 热更新失败原因（用于弹窗展示）
type HotUpdateFailureReason = {
  // 错误标题（如：下载失败）
  title: string;
  // 用户可读的错误描述
  message: string;
  // 原始错误信息，便于排查
  raw?: string;
};

// 默认配置
const DEFAULT_CONFIG: HotUpdateConfig = {
  // 外部存储下的升级目录（与 D3_BASE_PATH/upgrade 对齐）
  externalStoragePath: 'Download/d3/upgrade',
  // 本地记录的版本文件名
  versionFileName: 'version.json',
  // 定时检查间隔（毫秒），默认 30 分钟
  checkInterval: 30 * 60 * 1000,
  // 是否启用周期性检查
  enabled: true,
  // 默认：下载前弹窗确认
  confirmMode: 'before-download',
};

/** 热更新管理器（单例） */
export class HotUpdateManager {
  private static instance: HotUpdateManager;
  /** 运行时配置（可由 init 覆盖部分字段） */
  private config: HotUpdateConfig;
  /** 当前展示/对比用的版本号（会随 Capgo bundle 刷新） */
  private currentVersion: string;
  /** 是否正在执行 checkForUpdates，避免并发检查 */
  private isChecking = false;
  /** 是否正在执行 performUpdate，避免并发下载/应用 */
  private isUpdateRunning = false;
  /** 周期性检查的定时器句柄 */
  private checkTimer: number | null = null;

  private constructor() {
    this.config = { ...DEFAULT_CONFIG };
    this.currentVersion = this.getCurrentAppVersion();
  }

  /** 获取单例实例 */
  public static getInstance(): HotUpdateManager {
    if (!HotUpdateManager.instance) {
      HotUpdateManager.instance = new HotUpdateManager();
    }
    return HotUpdateManager.instance;
  }

  /**
   * 初始化热更新管理器。
   *
   * 当前采用“两阶段”策略：
   * 1. 应用启动阶段只做远端版本检查，不主动申请存储权限，不主动创建外部目录。
   * 2. 只有检测到新版本且用户在弹窗中确认后，才去申请存储权限并准备本地目录。
   *
   * 这样可以保证：
   * - 没有新版本时，不会因为热更新而触发存储权限申请。
   * - 有新版本时，先向用户说明用途，再进入权限与下载流程。
   */
  async init(config?: Partial<HotUpdateConfig>): Promise<void> {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    if (!Capacitor.isNativePlatform()) {
      console.warn('热更新仅支持原生平台，跳过初始化');
      return;
    }

    await this.refreshCurrentVersionFromRuntime();
    await this.checkForUpdates();

    if (this.config.enabled) {
      this.startPeriodicCheck();
    }
  }

  // 对外：获取用于 UI 展示的版本号
  // - 已应用热更：返回 bundle.version
  // - 未应用热更：返回 native（原生 versionName）
  // - 兜底：VITE_APP_VERSION
  public async getDisplayedVersion(): Promise<string> {
    await this.refreshCurrentVersionFromRuntime();
    return this.currentVersion;
  }

  // 对外：获取版本详情（展示版本、原生版本、当前 bundle 信息）
  public async getVersionDetails(): Promise<{ displayed: string; native: string; bundleId?: string; bundleVersion?: string }> {
    const fallback = this.getCurrentAppVersion();
    try {
      const current = await CapacitorUpdater.current();
      const bundleId = (current as any)?.bundle?.id as string | undefined;
      const bundleVersion = (current as any)?.bundle?.version as string | undefined;
      const nativeVersion = ((current as any)?.native as string | undefined) || fallback;
      const isBuiltin = String(bundleId || '').toLowerCase() === 'builtin' || String(bundleVersion || '').toLowerCase() === 'builtin';
      const displayed = (!isBuiltin && bundleVersion && String(bundleVersion).trim()) || nativeVersion || fallback;
      return { displayed, native: nativeVersion, bundleId, bundleVersion };
    } catch {
      return { displayed: fallback, native: fallback };
    }
  }

  /**
   * 检查是否有可用更新。
   * - 无新版本：不申请存储权限、不弹窗
   * - 有新版本：按 confirmMode 决定是否弹窗，用户确认后再 prepareStorageForUpdate
   */
  public async checkForUpdates(): Promise<boolean> {
    if (this.isChecking) {
      console.info('热更新检查已在进行中，跳过本次请求');
      return false;
    }

    this.isChecking = true;

    try {
      const versionInfo = await this.fetchVersionInfoFromApi();
      if (!versionInfo) {
        return false;
      }

      const targetVersion = this.resolveTargetVersion(versionInfo);
      const hasExplicitVersion = !!versionInfo.version;
      // 有显式 version：语义化比较；无 version：默认认为需要更新（依赖 downloadUrl/updateTime 哈希）
      const needsUpdate = hasExplicitVersion
        ? this.compareVersions(targetVersion, this.currentVersion)
        : true;

      if (!needsUpdate) {
        console.info('热更新检查完成：当前已是最新版本，无需申请存储权限');
        return false;
      }

      const mode = this.config.confirmMode || 'before-download';
      // 强制/静默模式：跳过确认弹窗，直接进入存储准备与下载
      if (mode === 'force' || mode === 'silent') {
        const storageReady = await this.prepareStorageForUpdate();
        if (!storageReady) {
          return false;
        }
        return await this.performUpdate(versionInfo);
      }

      // 默认及 after-download（当前与 before-download 等价）：先弹窗确认
      const handler = this.config.confirmHandler || this.defaultConfirmHandler.bind(this);
      const decision = await handler(versionInfo);
      if (decision !== 'accept') {
        return false;
      }

      // 用户确认后才申请权限并创建目录
      const storageReady = await this.prepareStorageForUpdate();
      if (!storageReady) {
        (versionInfo as any)._cleanupUI?.();
        return false;
      }

      (versionInfo as any)._setUiStage?.('downloading');
      return await this.performUpdate(versionInfo);
    } catch (error) {
      console.error('检查热更新失败', error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * 获取版本更新历史列表（当前版本 + 远端可用版本，供设置页展示）
   */
  public async fetchHistory(): Promise<VersionInfo[]> {
    const current = await this.getDisplayedVersion();
    const remote = await this.fetchVersionInfoFromApi();
    const list: VersionInfo[] = [];

    list.push({
      version: current,
      downloadUrl: '',
      updateTime: '',
      description: i18n.global.t('hotUpdate.currentVersionhis'),
    });

    if (remote?.downloadUrl) {
      const remoteVersion = this.resolveTargetVersion(remote);
      if (remoteVersion !== current) {
        list.unshift({
          ...remote,
          version: remoteVersion,
        });
      }
    }

    return list;
  }

  /**
   * 切换到指定版本（手动触发下载并应用，会先准备存储目录）
   */
  public async switchToVersion(info: VersionInfo): Promise<boolean> {
    const storageReady = await this.prepareStorageForUpdate();
    if (!storageReady) {
      return false;
    }
    return await this.performUpdate(info);
  }

  /** 调试：列出热更新目录内容，用于排查权限/路径问题 */
  public async debugDirectoryAccess(): Promise<void> {
    const { basePath, directory } = getPlatformConfig();
    try {
      const entries = await Filesystem.readdir({ path: basePath, directory });
      console.info('热更新目录调试成功', {
        platform: Capacitor.getPlatform(),
        basePath,
        directory,
        files: entries.files?.length ?? 0,
      });
    } catch (error) {
      console.error('热更新目录调试失败', {
        platform: Capacitor.getPlatform(),
        basePath,
        directory,
        error,
      });
      throw error;
    }
  }

  /**
   * 默认确认弹窗：Vue Dialog + 进度条。
   * 在 info 上挂载 _updateUI / _setUiStage 等钩子，供 performUpdate 驱动 UI。
   */
  private async defaultConfirmHandler(info: VersionInfo): Promise<'accept' | 'decline' | 'later'> {
    let appInstance: any = null;
    let domContainer: HTMLElement | null = null;

    try {
      // 弹窗展示版本：优先 bundle，其次 native，避免 this.currentVersion 未刷新
      const current = await this.getDisplayedVersion();
      const latest = this.resolveTargetVersion(info);
      const needsStoragePermissionGuide = await this.shouldShowStoragePermissionGuide();

      const result = await new Promise<'accept' | 'decline'>((resolve) => {
        const container = document.createElement('div');
        domContainer = container;
        document.body.appendChild(container);

        // 使用 reactive 包装 info，进度/阶段变化时 UI 自动更新
        const reactiveInfo = reactive({
          ...info,
          confirmed: false,
          stage: 'prompt' as HotUpdateUiStage,
          errorTitle: '',
          errorMessage: '',
          errorRaw: '',
        });

        Object.assign(info, reactiveInfo);

        // performUpdate 调用：更新下载进度百分比
        (info as any)._updateUI = (percent: number) => {
          reactiveInfo.downloadPercent = percent;
        };

        // performUpdate 调用：切换 UI 阶段（downloading / failed 等）
        (info as any)._setUiStage = (stage: HotUpdateUiStage) => {
          reactiveInfo.stage = stage;
        };

        // performUpdate 调用：展示失败原因
        (info as any)._setDownloadError = (reason: HotUpdateFailureReason) => {
          reactiveInfo.errorTitle = reason.title;
          reactiveInfo.errorMessage = reason.message;
          reactiveInfo.errorRaw = reason.raw || '';
        };

        // 失败后阻塞等待用户点击「重试」或「取消」
        let retryOrCancelResolver: ((value: 'retry' | 'cancel') => void) | null = null;
        (info as any)._waitRetryOrCancel = () => new Promise<'retry' | 'cancel'>((r) => {
          retryOrCancelResolver = r;
        });
        (info as any)._resolveRetryOrCancel = (value: 'retry' | 'cancel') => {
          if (retryOrCancelResolver) {
            retryOrCancelResolver(value);
            retryOrCancelResolver = null;
          }
        };

        const app = createApp({
          render() {
            const stage = (reactiveInfo as any).stage as HotUpdateUiStage;
            const isPrompt = stage === 'prompt';
            const isStoragePermission = stage === 'storage-permission';
            const isDownloading = stage === 'downloading';
            const isFailed = stage === 'failed';

            return h(Dialog, {
              visible: true,
              showOverlay: false,
              showCancelButton: true,
              showConfirmButton: isPrompt || isFailed,
              cancelText: i18n.global.t('btn.cancel'),
              confirmText: isPrompt ? i18n.global.t('btn.confirm') : i18n.global.t('hotUpdate.retry'),
              cancelDisabled: false,
              confirmDisabled: isDownloading || isStoragePermission,
              title: i18n.global.t('hotUpdate.updatePromptTitle'),
              onConfirm: () => {
                if (isPrompt) {
                  // 用户确认更新：resolve accept，后续由 checkForUpdates 申请权限并下载
                  reactiveInfo.confirmed = true;
                  reactiveInfo.stage = 'storage-permission';
                  resolve('accept');
                  return;
                }

                if (isFailed) {
                  reactiveInfo.stage = 'downloading';
                  reactiveInfo.errorTitle = '';
                  reactiveInfo.errorMessage = '';
                  reactiveInfo.errorRaw = '';
                  (info as any)._resolveRetryOrCancel?.('retry');
                }
              },
              onCancel: () => {
                if (isPrompt) {
                  resolve('decline');
                } else {
                  // 下载/失败阶段取消：通知 performUpdate 终止并可选择 cleanup
                  try {
                    (info as any)._cancelUpdate?.();
                    (info as any)._resolveRetryOrCancel?.('cancel');
                  } catch {
                    // ignore
                  }
                }
                app.unmount();
                document.body.removeChild(container);
                domContainer = null;
              },
              'onUpdate:visible': (val: boolean) => {
                if (!val) {
                  if (isPrompt) {
                    resolve('decline');
                  } else {
                    try {
                      (info as any)._cancelUpdate?.();
                      (info as any)._resolveRetryOrCancel?.('cancel');
                    } catch {
                      // ignore
                    }
                  }
                  app.unmount();
                  document.body.removeChild(container);
                  domContainer = null;
                }
              },
            }, {
              default: () => [
                h('div', { style: 'font-weight:500;font-size:16px;margin-bottom:10px;' }, i18n.global.t('hotUpdate.newVersionFound', { version: latest })),
                h('div', {}, i18n.global.t('hotUpdate.currentVersion', { version: current })),
                reactiveInfo.description
                  ? h('div', { style: 'margin-top:10px;white-space:pre-wrap;' }, `${i18n.global.t('hotUpdate.updateDescription')}\n${reactiveInfo.description}`)
                  : null,
                needsStoragePermissionGuide && (isPrompt || isStoragePermission)
                  ? h('div', { style: 'margin-top:12px;font-size:13px;line-height:1.6;color:#374151;white-space:pre-wrap;' }, i18n.global.t('hotUpdate.storagePermissionPurpose'))
                  : null,
                h('div', { style: 'font-size:12px;margin-top:10px;' }, i18n.global.t('hotUpdate.restartWarning')),
                isStoragePermission
                  ? h('div', { style: 'margin-top:10px;font-size:12px;color:#6b7280;' }, i18n.global.t('hotUpdate.storagePermissionApplying'))
                  : null,
                isFailed
                  ? h('div', { style: 'margin-top:12px;color:#d14343;font-size:14px;line-height:1.4;' }, [
                      h('div', { style: 'font-weight:600;margin-bottom:6px;' }, reactiveInfo.errorTitle || i18n.global.t('hotUpdate.downloadFailedTitle')),
                      h('div', {}, reactiveInfo.errorMessage || i18n.global.t('hotUpdate.downloadFailed')),
                    ])
                  : null,
                reactiveInfo.downloadPercent !== undefined
                  ? h('div', { style: 'margin-top:15px;' }, [
                      h('div', { style: 'height:8px;background:#f3f4f6;border-radius:4px;overflow:hidden;' }, [
                        h('div', {
                          style: `height:100%;background:#0b74ff;width:${reactiveInfo.downloadPercent}%;transition:width 0.3s ease;`,
                        }),
                      ]),
                      h('div', { style: 'font-size:12px;color:#6b7280;text-align:right;margin-top:4px;' }, `${Math.round(reactiveInfo.downloadPercent)}%`),
                    ])
                  : null,
              ],
            });
          },
        });

        appInstance = app;
        app.mount(container);
      });

      // 用户接受更新时保留弹窗，供下载进度展示；由 performUpdate 结束时 _cleanupUI
      if (result === 'accept') {
        (info as any)._cleanupUI = () => {
          if (appInstance && domContainer) {
            appInstance.unmount();
            document.body.removeChild(domContainer);
            appInstance = null;
            domContainer = null;
          }
        };
      }

      return result;
    } catch (error) {
      console.info('用户取消或关闭更新确认', error);
      return 'decline';
    }
  }

  /**
   * 执行热更新核心流程：Capgo download → set/next → 持久化 version.json。
   * 失败时通过弹窗等待用户重试或取消。
   */
  private async performUpdate(info: VersionInfo): Promise<boolean> {
    if (this.isUpdateRunning) {
      console.warn('已有热更新任务执行中，忽略本次请求');
      return false;
    }

    this.isUpdateRunning = true;
    const updater = CapacitorUpdater as any;
    const targetVersion = this.resolveTargetVersion(info);

    try {
      (info as any)._setUiStage?.('downloading');
      (info as any)._updateUI?.(5);

      if (typeof updater.download !== 'function') {
        throw new Error('当前热更新插件不支持 download 方法');
      }

      // 使用 Capgo 下载 zip 并注册 bundle（url 保持服务端原始地址）
      const bundle = await updater.download({
        url: info.downloadUrl,
        version: targetVersion,
      });

      (info as any)._updateUI?.(80);

      // 应用 bundle：优先 set，兼容旧版 next
      if (typeof updater.set === 'function') {
        await updater.set(bundle);
      } else if (typeof updater.next === 'function') {
        await updater.next(bundle);
      } else {
        throw new Error('当前热更新插件不支持 set/next 方法');
      }

      (info as any)._updateUI?.(100);
      this.currentVersion = targetVersion;
      await this.persistVersionInfo(info, targetVersion);
      await toast(i18n.global.t('hotUpdate.restartingUpdateToast'), 2);
      (info as any)._cleanupUI?.();
      return true;
    } catch (error: any) {
      console.error('执行热更新失败', error);
      const reason = this.resolveFailureReason(error);
      (info as any)._setUiStage?.('failed');
      (info as any)._setDownloadError?.(reason);

      const action = await (info as any)._waitRetryOrCancel?.();
      if (action === 'retry') {
        this.isUpdateRunning = false;
        return await this.performUpdate(info);
      }

      (info as any)._cleanupUI?.();
      return false;
    } finally {
      this.isUpdateRunning = false;
    }
  }

  /** 将底层错误映射为用户可读的失败提示 */
  private resolveFailureReason(error: any): HotUpdateFailureReason {
    const raw = String(error?.message || error || '');
    const lower = raw.toLowerCase();

    if (lower.includes('timeout')) {
      return {
        title: i18n.global.t('hotUpdate.downloadFailedTitle'),
        message: i18n.global.t('hotUpdate.downloadTimeout'),
        raw,
      };
    }

    if (lower.includes('network') || lower.includes('failed to fetch')) {
      return {
        title: i18n.global.t('hotUpdate.downloadFailedTitle'),
        message: i18n.global.t('hotUpdate.downloadNetworkError'),
        raw,
      };
    }

    if (lower.includes('404') || lower.includes('not found')) {
      return {
        title: i18n.global.t('hotUpdate.downloadFailedTitle'),
        message: i18n.global.t('hotUpdate.downloadNotFound'),
        raw,
      };
    }

    if (lower.includes('space') || lower.includes('quota') || lower.includes('no such file')) {
      return {
        title: i18n.global.t('hotUpdate.downloadFailedTitle'),
        message: i18n.global.t('hotUpdate.downloadNoSpace'),
        raw,
      };
    }

    return {
      title: i18n.global.t('hotUpdate.downloadFailedTitle'),
      message: i18n.global.t('hotUpdate.downloadFailed'),
      raw,
    };
  }

  /**
   * 从服务端接口获取版本信息。
   * - 成功：code === 0 且 value 中含下载地址（ips）
   * - 失败：记录日志并返回 null，不抛错、不中断应用
   *
   * 本地调试可临时启用 VITE_HOTUPDATE_TEST_URL（需与 VITE_APP_VERSION 不同以触发更新）：
   * - VITE_HOTUPDATE_TEST_URL: 可访问的 dist.zip
   * - VITE_HOTUPDATE_TEST_VERSION / VITE_HOTUPDATE_TEST_DESC: 可选
   */
  private async fetchVersionInfoFromApi(): Promise<VersionInfo | null> {
    // const testUrl = (import.meta as any)?.env?.VITE_HOTUPDATE_TEST_URL as string | undefined;
    // if (testUrl?.trim()) {
    //   return {
    //     version: (import.meta as any)?.env?.VITE_HOTUPDATE_TEST_VERSION || 'hotupdate-test',
    //     downloadUrl: testUrl.trim(),
    //     updateTime: new Date().toISOString(),
    //     description: (import.meta as any)?.env?.VITE_HOTUPDATE_TEST_DESC,
    //   };
    // }

    const url = 'https://www.d3inf.com/iam/mobile/upgrade/check';
    try {
      const res = await fetch(url, {
        method: 'POST',
        cache: 'no-store' as RequestCache,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      } as RequestInit);

      const json: any = await res.json().catch(() => null);
      if (!json) {
        console.error('热更新接口返回非 JSON 数据');
        return null;
      }

      if (json.code !== 0) {
        console.error('热更新接口返回错误状态', { code: json.code, message: json.message || json.msg });
        return null;
      }

      // 接口字段映射：ver → version，ips → downloadUrl，mt → updateTime，sdesc → description
      const v = json.value || {};
      const version = v.ver;
      const downloadUrl = v.ips;
      const updateTime = v.mt;
      const description = v.sdesc;

      if (!downloadUrl) {
        console.error('热更新接口缺少下载地址 downloadUrl');
        return null;
      }

      return {
        version,
        downloadUrl,
        updateTime,
        description,
      };
    } catch (error) {
      console.error('调用热更新接口失败', error);
      return null;
    }
  }

  /**
   * 解析本次更新的目标版本号。
   * 接口未提供 version 时，基于 downloadUrl + updateTime 生成稳定的 remote-<hash>。
   */
  private resolveTargetVersion(info: VersionInfo): string {
    if (info.version && String(info.version).trim()) {
      return String(info.version).trim();
    }

    const basis = `${info.downloadUrl || ''}|${info.updateTime || ''}`;
    let hash = 0;
    for (let i = 0; i < basis.length; i += 1) {
      hash = ((hash << 5) - hash) + basis.charCodeAt(i);
      hash |= 0;
    }
    return `remote-${Math.abs(hash)}`;
  }

  /**
   * 比较版本号，判断 version1（远端）是否高于 version2（当前）。
   * @returns true 表示需要更新
   */
  private compareVersions(version1: string, version2: string): boolean {
    if (version1 === version2) {
      return false;
    }

    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);
    const maxLength = Math.max(v1Parts.length, v2Parts.length);

    for (let i = 0; i < maxLength; i += 1) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;
      if (v1 > v2) return true;
      if (v1 < v2) return false;
    }

    return version1 !== version2;
  }

  /**
   * 获取运行时当前版本：优先 Capgo 已激活的 bundle.version，否则 native。
   * 未应用热更时 bundle 为 builtin，应使用原生安装包版本对比。
   */
  private async getRuntimeCurrentVersion(): Promise<string> {
    try {
      const current = await CapacitorUpdater.current();
      const bundleId = (current as any)?.bundle?.id as string | undefined;
      const bundleVersion = (current as any)?.bundle?.version as string | undefined;
      const nativeVersion = (current as any)?.native as string | undefined;
      // builtin 不是语义化版本，回退到 native
      const isBuiltin = String(bundleId || '').toLowerCase() === 'builtin' || String(bundleVersion || '').toLowerCase() === 'builtin';
      const resolved = (!isBuiltin && bundleVersion && String(bundleVersion).trim()) || (nativeVersion && String(nativeVersion).trim());
      return resolved || this.getCurrentAppVersion();
    } catch {
      return this.getCurrentAppVersion();
    }
  }

  /** 从 Capgo 刷新 currentVersion，避免重复提示已应用的 bundle */
  private async refreshCurrentVersionFromRuntime(): Promise<void> {
    const runtimeVersion = await this.getRuntimeCurrentVersion();
    if (runtimeVersion && runtimeVersion !== this.currentVersion) {
      this.currentVersion = runtimeVersion;
    }
  }

  /**
   * 获取内置（原生）应用版本，来自 VITE_APP_VERSION。
   * 热更生效后应以 getRuntimeCurrentVersion 为准；此处作兜底。
   */
  private getCurrentAppVersion(): string {
    return import.meta.env.VITE_APP_VERSION || '1.0.0';
  }

  /**
   * 启动阶段仅探测升级目录是否已存在。
   *
   * 这里绝不主动创建目录，也不主动触发权限申请。
   * 真正的目录创建动作统一放到检测到新版本且用户确认后的流程中处理。
   */
  private async ensureDirectories(): Promise<void> {
    this.config.externalStoragePath = `${D3_BASE_PATH}/upgrade`;
    const exists = await checkUpgradeDirExists();
    if (!exists) {
      console.info('启动阶段未发现热更新目录，等待检测到新版本并经用户确认后再申请存储权限并创建目录');
    }
  }

  /**
   * 在用户已经确认更新之后，再按需申请存储权限并创建热更新目录。
   *
   * 这里是当前热更新流程中唯一会主动调用 `createFile()` 的位置。
   * 也就是说，应用启动时仅做接口检查；只有在检测到新版本并且用户点击确认后，
   * 才会进入下面的目录准备与权限申请逻辑。
   *
   * 当前实现不再依赖“先 mkdir 试试看”来判断权限，而是先显式检查：
   * - Android 11 及以上：先判断是否已具备“所有文件访问权限”，如未授权则打开系统设置页引导用户手动开启。
   * - Android 10 及以下：先发起系统运行时存储权限申请，授权成功后再创建目录。
   * - 只有显式授权确认成功后，才会继续执行 `createFile()` 创建 `Download/d3/upgrade` 目录。
   */
  private async ensureDirectoriesForUpdate(): Promise<boolean> {
    this.config.externalStoragePath = `${D3_BASE_PATH}/upgrade`;
    const exists = await checkUpgradeDirExists();
    if (exists) {
      return true;
    }

    try {
      const granted = await this.ensureStoragePermissionGranted();
      if (!granted) {
        return false;
      }

      await createFile();
      const created = await checkUpgradeDirExists();
      if (!created) {
        throw new Error('创建目录后仍无法访问升级目录');
      }
      return true;
    } catch (error) {
      console.error('检测到新版本后申请存储权限或创建热更新目录失败', error);
      try {
        await toast(i18n.global.t('hotUpdate.writePermissionDenied'), 3);
      } catch {
        // ignore
      }
      return false;
    }
  }

  /**
   * 显式确保 Android 外部存储权限已经准备好。
   *
   * 说明：
   * - 非 Android 平台不走这套权限逻辑，直接返回 true。
   * - Android 11+ 不会出现类似相机权限的标准弹窗，而是跳转到“所有文件访问权限”设置页。
   * - Android 10 及以下会走系统运行时权限弹窗。
   */
  private async ensureStoragePermissionGranted(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'android') {
      return true;
    }

    try {
      const currentStatus = await getAndroidStoragePermissionStatus();
      if (currentStatus.granted) {
        return true;
      }

      const result = await requestAndroidStoragePermission();
      if (result.granted) {
        return true;
      }

      const deniedMessage = result.requiresSettings
        ? i18n.global.t('hotUpdate.storagePermissionDeniedAfterSettings')
        : i18n.global.t('hotUpdate.storagePermissionDeniedRuntime');
      await toast(deniedMessage, 3);
      return false;
    } catch (error) {
      console.error('显式申请 Android 存储权限失败', error);
      await toast(i18n.global.t('hotUpdate.storagePermissionRequestFailed'), 3);
      return false;
    }
  }

  /** Android 且尚未授予存储权限时，在更新弹窗中展示权限用途说明 */
  private async shouldShowStoragePermissionGuide(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'android') {
      return false;
    }

    try {
      const status = await getAndroidStoragePermissionStatus();
      return !status.granted;
    } catch (error) {
      console.warn('检查热更新存储权限状态失败，按未授权处理', error);
      return true;
    }
  }

  /**
   * 将“目录存在 / 版本文件可写 / 旧目录兼容处理”统一封装为下载前准备。
   *
   * 这个方法只会在两种场景执行：
   * 1. 已检测到新版本，并且用户已经确认更新。
   * 2. 用户主动切换到某个历史版本。
   *
   * 因此它不会影响“无新版本时不申请权限”的启动体验。
   */
  private async prepareStorageForUpdate(): Promise<boolean> {
    const hasDirectoryAccess = await this.ensureDirectoriesForUpdate();
    if (!hasDirectoryAccess) {
      return false;
    }

    await this.ensureVersionFileWithBackoff();
    await this.cleanupLegacySubDirs();
    return true;
  }

  /**
   * 确保本地 version.json 存在（写入当前展示版本等元数据）。
   * 写入失败仅 warn，不阻断下载流程。
   */
  private async ensureVersionFileWithBackoff(): Promise<void> {
    const exists = await checkUpgradeDirExists();
    if (!exists) {
      return;
    }

    const versionPath = `${D3_BASE_PATH}/upgrade/${this.config.versionFileName}`;
    const payload = JSON.stringify({
      currentVersion: await this.getDisplayedVersion(),
      updatedAt: new Date().toISOString(),
    });

    try {
      await this.writePlatformFile(versionPath, payload);
    } catch (error) {
      console.warn('写入热更新版本文件失败，已忽略', error);
    }
  }

  private async cleanupLegacySubDirs(): Promise<void> {
    // 兼容旧版本目录结构的保留钩子。
    // 当前仅在用户确认更新并且目录已准备好之后才允许触达公共目录，
    // 避免在应用冷启动阶段因热更新检查而产生额外的存储访问。
  }

  /** 按 checkInterval 启动周期性更新检查 */
  private startPeriodicCheck(): void {
    if (this.checkTimer) {
      window.clearInterval(this.checkTimer);
    }

    this.checkTimer = window.setInterval(() => {
      void this.checkForUpdates();
    }, this.config.checkInterval);
  }

  /** 热更成功后，将目标版本信息写入本地 version.json */
  private async persistVersionInfo(info: VersionInfo, version: string): Promise<void> {
    const exists = await checkUpgradeDirExists();
    if (!exists) {
      return;
    }

    const versionPath = `${D3_BASE_PATH}/upgrade/${this.config.versionFileName}`;
    const payload = JSON.stringify({
      version,
      downloadUrl: info.downloadUrl,
      updateTime: info.updateTime,
      description: info.description,
      updatedAt: new Date().toISOString(),
    });

    try {
      await this.writePlatformFile(versionPath, payload);
    } catch (error) {
      console.warn('持久化热更新版本信息失败，已忽略', error);
    }
  }

  /**
   * 按平台配置写入文件（Harmony/Android/iOS 目录策略由 getPlatformConfig 决定）。
   */
  private async writePlatformFile(path: string, data: string): Promise<void> {
    const { directory } = getPlatformConfig();
    await Filesystem.writeFile({
      path,
      data,
      directory,
      encoding: Encoding.UTF8,
      recursive: true,
    });
  }
}

// 导出单例，供 main.ts 等入口初始化
export const hotUpdateManager = HotUpdateManager.getInstance();
